import { useId } from 'react';

interface ProgressCircleProps {
    progress: number;
    taken?: number;
    current?: number;
    required?: number;
    /** Explicit size in px. If omitted, uses variant sizing. */
    size?: number;
    /** Explicit stroke width in px. If omitted, derived from size. */
    strokeWidth?: number;
    showText?: boolean;
    /** Visual size variant used when size is not provided. */
    variant?: 'xs' | 'sm' | 'md' | 'lg';
    /** Optional override for the inner text color. */
    textClassName?: string;
    /** Optional override for the background track color. */
    trackClassName?: string;
    className?: string;
}

export default function ProgressCircle({ 
    progress, 
    taken, 
    current,
    required, 
    size,
    strokeWidth,
    showText = true,
    variant = 'md',
    textClassName,
    trackClassName,
    className = ""
}: ProgressCircleProps) {
    const id = useId();
    
    // Calculate progress values
    let clampedProgress = Math.max(0, Math.min(1, progress ?? 0));
    let takenProgress = 0;
    let currentProgress = 0;
    
    // If current is provided, calculate separate progress for taken and current
    if (current !== undefined && taken !== undefined && required !== undefined && required > 0) {
        takenProgress = Math.max(0, Math.min(1, taken / required));
        currentProgress = Math.max(0, Math.min(1, (taken + current) / required));
        clampedProgress = currentProgress; // Total progress includes both taken and current
    } else {
        clampedProgress = Math.max(0, Math.min(1, progress ?? 0));
    }
    
    const computedSize = size ?? (
        variant === 'xs' ? 18 : 
        variant === 'sm' ? 32 : 
        variant === 'lg' ? 40 : 
        35
    );
    const computedStroke = strokeWidth ?? (
        variant === 'xs' ? 2.5 :
        computedSize >= 40 ? 3 : 
        computedSize >= 32 ? 2 : 
        1.5
    );
    const radius = (computedSize - computedStroke) / 2;
    const circumference = 2 * Math.PI * radius;
    const strokeDashoffset = circumference * (1 - clampedProgress);
    const takenStrokeDashoffset = circumference * (1 - takenProgress);
    
    // For the current (amber) segment, calculate the length and offset
    const currentSegmentLength = circumference * (currentProgress - takenProgress);
    const currentStrokeDashoffset = -circumference * takenProgress;
    
    const titleText = taken !== undefined && required !== undefined
        ? `${taken}/${required}`
        : `${Math.round(clampedProgress * 100)}%`;

    const textSizeClass = 
        variant === 'xs' ? 'text-[8px]' : 
        variant === 'sm' ? 'text-[9px]' : 
        variant === 'lg' ? 'text-xs' : 
        'text-[11px]';
    const dynamicTextColor = textClassName ?? (
        clampedProgress >= 1 ? 'text-emerald-700' : clampedProgress === 0 ? 'text-gray-500' : 'text-gray-800'
    );
    const bgTrackClass = trackClassName ?? 'text-gray-300';

    return (
        <div className={`relative inline-flex items-center justify-center ${className}`}>
            <svg 
                className={`transform -rotate-90`}
                width={computedSize} 
                height={computedSize}
                role="img"
                aria-label={`Progress ${titleText}`}
            >
                <defs>
                    <linearGradient id={`pc-gradient-${id}`} x1="0%" y1="0%" x2="0%" y2="100%">
                        <stop offset="0%" stopColor="#34d399" />
                        <stop offset="100%" stopColor="#10b981" />
                    </linearGradient>
                    <linearGradient id={`pc-gradient-amber-${id}`} x1="0%" y1="0%" x2="0%" y2="100%">
                        <stop offset="0%" stopColor="#FFB300" />
                        <stop offset="100%" stopColor="#FFB300" />
                    </linearGradient>
                </defs>
                {/* Background circle */}
                <circle
                    cx={computedSize / 2}
                    cy={computedSize / 2}
                    r={radius}
                    strokeWidth={computedStroke}
                    stroke="currentColor"
                    fill="none"
                    className={bgTrackClass}
                />
                {current !== undefined && taken !== undefined && required !== undefined ? (
                    <>
                        {/* Taken progress circle (green) */}
                        <circle
                            cx={computedSize / 2}
                            cy={computedSize / 2}
                            r={radius}
                            strokeWidth={computedStroke}
                            stroke={`url(#pc-gradient-${id})`}
                            fill="none"
                            className="transition-[stroke-dashoffset] duration-300 ease-in-out"
                            strokeDasharray={circumference}
                            strokeDashoffset={takenStrokeDashoffset}
                            strokeLinecap="round"
                        />
                        {/* Current progress circle (amber) - only shows between taken and current */}
                        {current > 0 && (
                            <circle
                                cx={computedSize / 2}
                                cy={computedSize / 2}
                                r={radius}
                                strokeWidth={computedStroke}
                                stroke={`url(#pc-gradient-amber-${id})`}
                                fill="none"
                                className="transition-all duration-300 ease-in-out"
                                strokeDasharray={`${currentSegmentLength} ${circumference}`}
                                strokeDashoffset={currentStrokeDashoffset}
                                strokeLinecap="round"
                            />
                        )}
                    </>
                ) : (
                    /* Single progress circle */
                    <circle
                        cx={computedSize / 2}
                        cy={computedSize / 2}
                        r={radius}
                        strokeWidth={computedStroke}
                        stroke={`url(#pc-gradient-${id})`}
                        fill="none"
                        className="transition-[stroke-dashoffset] duration-300 ease-in-out"
                        strokeDasharray={circumference}
                        strokeDashoffset={strokeDashoffset}
                        strokeLinecap="round"
                    />
                )}
            </svg>
            {showText && (
                <div className="absolute inset-0 flex items-center justify-center">
                    <span className={`${textSizeClass} font-medium ${dynamicTextColor}`}>
                        {taken !== undefined && required !== undefined ? (
                            <>{taken}/{required}</>
                        ) : (
                            <>{Math.round(clampedProgress * 100)}%</>
                        )}
                    </span>
                </div>
            )}
        </div>
    );
} 